//////////////////
// IMPORTS
//////////////////

// libraries
import React from "react";
import { useQuery, useMutation } from "@apollo/client";

import { WebSettingsEmailsAndNotificationsQueryDocument } from "./__generated__/WebSettingsEmailsAndNotifications.generated";
import { UpdateNotificationSettingMutationDocument } from "./__generated__/UpdateNotificationSetting.generated";
import {
  receiveDefragMoveValueSummaryEmailSetting,
  receiveInviteSuggestionsEmailSetting,
  receiveNowOnAutopilotEmailSetting,
  receiveWeekInReviewEmailSetting,
  sendNowOnAutopilotEmailSetting,
} from "./web-settings-emails-and-notifications";
import {
  WebSettingsSections,
  AccountSections,
  IntegrationsSections,
  createSettingsViewId,
} from "../web-settings";

// components
import { WebSettingsContainer } from "../web-settings-container";

import { A } from "@components/ui";

// constants
// import { paths } from "@clockwise/client-commons/src/constants/site";

// util
import { getCurrentOrg } from "@util/org.util";
import { page, PageEvents, track, TrackingEvents } from "@util/analytics.util";
import { logger } from "@util/logger.util";

// material-ui
import { Checkbox, FormControlLabel, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";

// ~-~-~-~-~-~-~-
// Web Settings Emails and notifications
// ~-~-~-~-~-~-~-
interface IProps {
  onChangeSettingsView: (settingsViewId: string) => void;
}

const useStyles = makeStyles({
  h1: {
    marginBottom: 44,
  },
  h3: {
    marginBottom: 26,
  },
  section: {
    marginBottom: 69,
  },
});

export const WebSettingsEmailsAndNotifications = (props: IProps) => {
  const classes = useStyles();

  const [updateSetting] = useMutation(UpdateNotificationSettingMutationDocument, {
    onError: (error) => {
      logger.error(`failed to updateSetting: ${error}`);
    },
  });

  const updateEmailAndNotificationSetting = (
    e: React.ChangeEvent<HTMLInputElement>,
    setting: any,
  ) => {
    const synchronousUpdateSetting = async () => {
      await updateSetting({
        variables: {
          input: { setting, value: [e.target.checked ? "true" : "false"] },
        },
      });
    };

    synchronousUpdateSetting();
  };

  React.useEffect(() => {
    page(PageEvents.WEB_SETTINGS_EMAILS_AND_NOTIFICATIONS);
  }, []);

  const { loading, data, error } = useQuery(WebSettingsEmailsAndNotificationsQueryDocument);

  if (error) {
    logger.error(`failed to load WebSettingsEmailsAndNotifications: ${error}`);
    return (
      <WebSettingsContainer>
        An error occurred while loading Clockwise:
        <br />
        {error.message}
      </WebSettingsContainer>
    );
  }

  if (!data || loading) {
    return <WebSettingsContainer>Loading…</WebSettingsContainer>;
  }

  const viewer = data.viewer;
  const org = getCurrentOrg(viewer);

  if (!org) {
    return <WebSettingsContainer>Loading…</WebSettingsContainer>;
  }

  // ~-~-~-~-~-~-~-
  // Helper
  // ~-~-~-~-~-~-~-
  const onClickSlack = () => {
    track(TrackingEvents.WEB_SETTINGS.EMAILS_AND_NOTIFICATIONS.SLACK_NOTIFICATIONS_LINK_CLICKED);
    props.onChangeSettingsView(
      createSettingsViewId(
        WebSettingsSections.Account,
        AccountSections.Integrations,
        IntegrationsSections.Slack,
      ),
    );
  };

  const onChange = (setting: any, event: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    track(event, { isOn: e.target.checked });
    updateEmailAndNotificationSetting(e, setting);
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  return (
    <WebSettingsContainer>
      <Typography variant="h1" className={classes.h1}>
        Emails and notifications
      </Typography>
      <div className={classes.section}>
        <Typography variant="h3" className={classes.h3}>
          Flexible meeting emails
        </Typography>
        <div>
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={sendNowOnAutopilotEmailSetting.isOn(org)}
                onChange={onChange(
                  sendNowOnAutopilotEmailSetting.get(org)!,
                  TrackingEvents.WEB_SETTINGS.EMAILS_AND_NOTIFICATIONS
                    .TOGGLE_SEND_NOW_ON_AUTOPILOT_EMAIL_SETTING,
                )}
              />
            }
            label={"Notify non-Clockwise attendees when you mark a meeting as flexible"}
          />
        </div>
        <div>
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={receiveNowOnAutopilotEmailSetting.isOn(org)}
                onChange={onChange(
                  receiveNowOnAutopilotEmailSetting.get(org)!,
                  TrackingEvents.WEB_SETTINGS.EMAILS_AND_NOTIFICATIONS
                    .TOGGLE_RECEIVE_NOW_ON_AUTOPILOT_EMAIL_SETTING,
                )}
              />
            }
            label={"Get notified when others mark a meeting you're attending as flexible"}
          />
        </div>
        <div>
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={receiveDefragMoveValueSummaryEmailSetting.isOn(org)}
                onChange={onChange(
                  receiveDefragMoveValueSummaryEmailSetting.get(org)!,
                  TrackingEvents.WEB_SETTINGS.EMAILS_AND_NOTIFICATIONS
                    .TOGGLE_RECEIVE_DEFRAG_MOVE_VALUE_SUMMARY_EMAIL_SETTING,
                )}
              />
            }
            label={"Summary of your flexible meeting updates"}
          />
        </div>
      </div>
      <div className={classes.section}>
        <Typography variant="h3" className={classes.h3}>
          Other emails
        </Typography>
        <div>
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={receiveWeekInReviewEmailSetting.isOn(org)}
                onChange={onChange(
                  receiveWeekInReviewEmailSetting.get(org)!,
                  TrackingEvents.WEB_SETTINGS.EMAILS_AND_NOTIFICATIONS
                    .TOGGLE_RECEIVE_WEEK_IN_REVIEW_EMAIL_SETTING,
                )}
              />
            }
            label={"Your week in review"}
          />
        </div>
        <div>
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={receiveInviteSuggestionsEmailSetting.isOn(org)}
                onChange={onChange(
                  receiveInviteSuggestionsEmailSetting.get(org)!,
                  TrackingEvents.WEB_SETTINGS.EMAILS_AND_NOTIFICATIONS
                    .TOGGLE_RECEIVE_INVITE_SUGGESTIONS_EMAIL_SETTING,
                )}
              />
            }
            label={"Suggestions for inviting coworkers"}
          />
        </div>
      </div>
      <div className={classes.section}>
        <Typography variant="h3" className={classes.h3}>
          Slack notifications
        </Typography>
        <Typography variant="body1">
          <A onClick={onClickSlack}>Go to Clockwise in Slack</A> to manage or turn off your Slack
          notifications.
        </Typography>
      </div>
    </WebSettingsContainer>
  );
};
